import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { fetchFilteredGuides, fetchGuides, fetchGuideTypes, getGuideTypeLabel } from "./utils";
import GuideFilter from "./GuideFilter";
import mapboxgl from "mapbox-gl"
import countries from "./countries.json"
import SocialLinkIcon from "./SocialLinkIcon";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY

const MaboxGuideProfiles = () => {

    const [guides, setGuides] = useState([])
    const [guideTypes, setGuideTypes] = useState([])
    const [map, setMap] = useState(undefined)
    const [theConversationId, setTheConversationId] = useState(null);
    const [isInteractingWithMap, setInteractingWithMap] = useState(false)
    const [currentIndex , setIndex] = useState(null)
    const containerRef = useRef(null);
    const mapLoaded = useRef(false);
    const [mouseOVer, setMouseOver] = useState(false)
    const [showFilter, setShowFilter] = useState(false);

    let { e_ph } = useParams();

    const getInitialFilterValues = () => {
        const query = new URLSearchParams(window.location.search);
        return {
            age: query.get("age") || "",
            gender: query.get("gender") || "",
            archetype: query.get("archetype") || query.get("guideType") || "",
            country: query.get("country") || ""
        };
    }

    const initialFilterValues = getInitialFilterValues();
    const hasInitialFilters = Object.values(initialFilterValues).some(Boolean);

    const isMobile = () => {
        return (window.innerWidth < 800 )
    }

    const renderAge = (age) => {
        switch (age) {
            case 'over_65':
                return '65+'
            case 'under_18':
                return '< 18'
            default:
                return age
        }
    }

    const renderGender = (gender) => {
        return `${gender.charAt(0).toUpperCase()}${gender.slice(1)}`
    }

	const initializeMap = async () => {
		if (!map) {
			let theMap = undefined
            let zoom = isMobile() ? 1 : 2.5
			// create map
			const createTheMap = new Promise((resolve, reject) => {
				try {
					theMap = new mapboxgl.Map({
						container: 'guideProfilesMap', // container ID
                        projection: 'globe',
						style: 'mapbox://styles/redappledigitalhealth/cmlfvmstf00e001sl2daj9d7i', // style URL
						center: [-117.8032302, 33.7171214], // starting position [lng, lat]
						zoom: zoom, // starting zoom
					})
					resolve(theMap)
				} catch (error) {
					console.log(error)
					resolve(false)
				}
			})
            // The Boggler Look
			createTheMap.then((theMap) => {

                theMap.on('style.load',() => {
                    theMap.setFog({
                        color: '#f2fad5', // Lower atmosphere
                        'high-color': '#fdfbf7', // Upper atmosphere
                        'horizon-blend': 0.2, // Atmosphere thickness (default 0.2 at low zooms)
                        'space-color': '#065e76', // Background color
                        'star-intensity': 1.0 // Background star brightness (default 0.35 at low zoooms )
                    });
                    let padding = isMobile() ? {bottom: 300} : {right: 300} 
                    theMap.easeTo({padding: padding})
                })

				setMap(theMap)

                function spinGlobe() {
                    const zoom = theMap.getZoom();
                    const secondsPerRevolution = 120;
                    const maxSpinZoom = 3;
                    const slowSpinZoom = 0;
                    
                        let distancePerSecond = 360 / secondsPerRevolution;
                        if (zoom > slowSpinZoom && !isInteractingWithMap) {
                            // Slow spinning at higher zooms
                            const zoomDif =
                                (maxSpinZoom - zoom) / (maxSpinZoom - slowSpinZoom);
                            distancePerSecond *= zoomDif;
                        }
                        const center = theMap.getCenter();
                        center.lng -= distancePerSecond;
                        // Smoothly animate the map over one second.
                        // When this animation is complete, it calls a 'moveend' event.
                        theMap.easeTo({ center, duration: 1000, easing: (n) => n });
                    
                }
                theMap.on('moveend', spinGlobe)

                theMap.on()
			})
		}
	}

    const getPaymentStatus = async () => {
        fetch(process.env.REACT_APP_API_URL + "/stripe/guide-payment-status/" + e_ph, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify()
        })
        .then((response) => {
            return response.json();
        })
        .then((data) => {
            let { twilio_conversation_id } = data;
            if(twilio_conversation_id) {
                setTheConversationId(twilio_conversation_id);
            }
        })
        .catch((error) => {
            console.error("Error marking Fairy Godmother payment as paid:", error);
        });
    }

    const renderGuideTypeBadgesForGuide = (guide, i) => 
        guide.types.map((t,ti)=> ti > 0 && (
            <div key={i + ti} className={`archetype badge-${ti}`} style={{backgroundImage:`url(${guideTypes.find(gt => gt.hyphenated_title === guide.types[ti])?.imageUrl})`}}></div>
    ))
    

    const updateMap = (guide) => {
        let country = countries.find(c=> c.code === guide.country.code)
        let lngLat = [country.lng, country.lat]
        let padding = isMobile() ? {bottom: 300} : {right: 500} 
        let zoom = isMobile() ? 1 : 2.5

        setInteractingWithMap(true)
        map.flyTo({
            center: lngLat,
            zoom: zoom,
            duration: 1000,
            padding: padding
        })
        map.timeout = setTimeout( () => setInteractingWithMap(false), 1000)
    }

    useEffect(() => {
        if (mapLoaded.current) return
        (async () => {
            mapLoaded.current = true
            const filterParams = new URLSearchParams();
            if(initialFilterValues.age) filterParams.append("age", initialFilterValues.age);
            if(initialFilterValues.gender) filterParams.append("gender", initialFilterValues.gender);
            if(initialFilterValues.archetype) filterParams.append("archetype", initialFilterValues.archetype);
            if(initialFilterValues.country) filterParams.append("country", initialFilterValues.country);

            const fetchedGuides = hasInitialFilters ? await fetchFilteredGuides(filterParams) : await fetchGuides()
            const fetchedGuideTypes = await fetchGuideTypes()
            setGuides(fetchedGuides?.guides)
            setGuideTypes(fetchedGuideTypes)
            setShowFilter(hasInitialFilters)
            initializeMap()
            getPaymentStatus()
        })()
    }, []);


    useEffect(() => {
        const container = containerRef.current;
        const children = Array.from(container.children);
        if (children.length < 1) return
        const observer = new IntersectionObserver(
            (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const index = children.indexOf(entry.target);
                    setIndex(index)
                    updateMap(guides[index])
                }
            });
            },
            { 
                root: container,
                threshold: 0.6, // adjust sensitivity (60% visible)
            }
        );

        children.forEach((child) => observer.observe(child));

        return () => observer.disconnect();
    }, [guides]);

    const handleChooseGuide = (guide) => {
        const guideID = guide?._id?.toString();
        if(!guideID) return;

        const customerID = e_ph || window.localStorage.getItem("p") || window.localStorage.getItem("encrypted_phone_number");
        const conversationID = theConversationId || window.localStorage.getItem("twilio_conversation_id");
        const guideType = guide?.types?.[0] || guide?.guideType || "0";

        if(customerID && conversationID) {
            window.open(`/t/${conversationID}/pay/${customerID}/guide-intro-call/${guideType}/${guideID}`, "_self");
        } else if(guide?.username) {
            window.open("/@"+guide.username, "_self");
        } else {
            window.open(`/profile/${guideID}`, "_self");
        }
    }

    return (
        <>
            <Header />
            <section className={`main-body-section map${mouseOVer ? ' hover' : ''}`} >
                <button className={`filter-button boggler${showFilter ? ' active' : ''}`} onClick={() => setShowFilter(prev => !prev)}>
                    {showFilter ? "Hide Filters" : "Show Filters"}
                </button>
                {showFilter && <GuideFilter initialValues={initialFilterValues} onFilter={(guides) => setGuides(guides)} onClose={() => setShowFilter(false)} />}
                <div id="guide-list-container" ref={containerRef} onMouseEnter={(e)=>setMouseOver(true)} onMouseLeave={(e)=>setMouseOver(false)}>
                    {guides && guides.length > 0 && guides.map((g,i) => (
                        <div key={'guide-' + i} className={`guide-item list-item${i === currentIndex ? ' selected' : ''}`}>
                            <div className="profile-pic-container" style={{
                                backgroundImage: `url(${guideTypes.find(gt => gt.hyphenated_title === g.types[0])?.imageUrl})`
                            }}>
                                <img className="profile-picture" src={guideTypes.find(gt => gt.hyphenated_title === g.types[0])?.imageUrl} />
                            </div>
                            {renderGuideTypeBadgesForGuide(g,i)}
                            <div className="guide-left left">
                                <span className="badge gender">{renderGender(g.gender)}</span>
                                <span className="badge age">My Age: {renderAge(g.age)}</span>
                                <span className="country">
                                    <div>{g.country.name.length > 13 
                                        ? g.country.code 
                                        : g.country.name}</div>
                                    <div className="flag">{g.country.flag}</div>
                                </span>
                                <span className="arch-list">
                                    {g.types.map((label,ti)=> (
                                        <span key={'archetype-label-' + i + ti} className={`archetype-badge badge`}>{getGuideTypeLabel(label)}</span>)
                                    )}
                                </span>
                            </div>
                            <div className="right">
                                <span className="name"><p className="lucky-font">{g.name.split(' ')[0]}</p></span>
                                <div>
                                    {g?.work && g?.work !== "student" && g?.work !== "retired" && g?.work !== "unemployed" && <p>{`I work in the ${g.work} industry`}</p>}
                                    {g?.work && g?.work == "student" && <p>{`I am a student`}</p>}
                                    {g?.work && g?.work == "retired" && <p>{`I am retired`}</p>}
                                    {g?.work && g?.work == "unemployed" && <p>{`The Boggler is my jam!`}</p>}
                                    <div className="motivation" style={{maxHeight: "150px", overflowY: "scroll"}}>
                                        <p>{g.motivation}</p>
                                    </div>
                                </div>
                                <div className="social-media-container">
                                    {g.social_links && g.social_links.map((l,li) => (
                                        <SocialLinkIcon
                                            size={40}
                                            onClick={() => window.open(l.url, "_blank")}
                                            platform={l.platform} 
                                            key={'social-link-' + i + li} 
                                            link={l} 
                                        />
                                    ))}
                                </div>
                            </div>
                            <button className="manage-item-action boggler" onClick={() => handleChooseGuide(g)}>
                                <span style={{alignContent: 'center', margin: "0 auto"}}>Choose</span>
                            </button>
                        </div>
                    ))}
                </div>
                <div id="guideProfilesContainer">
                    <div id="guideProfilesMap">
                        {guides.length > 0 && (<div className={`arrow up${currentIndex !== 0 ? ' active': ''}`}></div>)}
                        {guides.length > 0 && (<div className={`arrow down${currentIndex !== guides.length - 1 ? ' active': ''}`}></div>)}
                            
                    </div>
                </div>
            </section>
            <Footer />
        </>
    );
};

export default MaboxGuideProfiles;
